import { limit } from "@giftforward/entitlements";
import OneSchemaImporter, { type OneSchemaTemplateOverrides } from "@oneschema/react";
import { useEffect, useRef, useState } from "react";
import { isProduction, PUBLIC_ENV } from "@/config/publicEnv";
import { CONFIGS } from "@/constants/GfgModal";
import { type Recipient, useCampaignBuilder } from "@/context/CampaignBuilderContext";
import { useMyEntitlements } from "@/hooks/useMyEntitlements";
import { useOneSchemaToken } from "@/hooks/useOneSchemaToken";
import { matchCountryCode } from "@/lib/country";
import { recipientTemplateIsPro } from "@/lib/recipientTemplate";
import { normalizeSendDate, normalizeSendTime } from "@/utils/sendSchedule-helper";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onImport: (recipients: Recipient[]) => void;
}

/** The fade-out before unmount. The `duration-270` classes below stay literal: Tailwind needs static names. */
export const MODAL_FADE_MS = 270;

type OneSchemaRecord = Record<string, string | undefined>;

const rawOrUndefined = (value: string | undefined) => value?.trim() || undefined;

// The Starter and Pro templates live in OneSchema's dashboard and mark the last names required.
// Last names are optional per GFG-367, so override both columns on every launch (both templates).
// is_required lets a cell be blank; must_exist lets the column go unmapped and drops the asterisk
// OneSchema shows on Map columns.
const LAST_NAME_OVERRIDES = [
  { key: "recipient_last_name", is_required: false, must_exist: false },
  { key: "sender_last_name", is_required: false, must_exist: false },
];
// A manual campaign never sends per-recipient, so the Pro template's Send Date/Time columns are
// irrelevant — make them skippable (unmappable, never required) so a "link to a list" import isn't
// forced to fill a schedule GfG will never act on. Any value that is mapped is dropped on import.
const MANUAL_SEND_OVERRIDES = [
  { key: "send_on_date", is_required: false, must_exist: false },
  { key: "send_at_time", is_required: false, must_exist: false },
];

// The dashboard's font URL is a Google Fonts specimen page, not a stylesheet, so the browser blocks
// it and the importer falls back to Times. Point it at the Lato stylesheet the app already loads.
const IMPORTER_FONT = {
  fontUrl: "https://fonts.googleapis.com/css2?family=Lato:wght@400;700&display=swap",
  fontFamily: "Lato",
};

export function BulkUploadModal({ isOpen, onClose, onImport }: Props) {
  const [visible, setVisible] = useState(false);
  const [animated, setAnimated] = useState(false);
  const rafRef = useRef<number | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { data: userJwt } = useOneSchemaToken(isOpen);
  const { ent } = useMyEntitlements();
  // A manual campaign never sends per-recipient (GfG distributes nothing), so any Send Date/Time the
  // Pro template collected is meaningless — drop it on import rather than store a value that would
  // only ever be hidden and ignored, and don't force the importer to collect it in the first place.
  const { notificationMode } = useCampaignBuilder();
  const isManual = notificationMode === "manual";
  const templateOverrides: OneSchemaTemplateOverrides = {
    columns_to_update: isManual
      ? [...LAST_NAME_OVERRIDES, ...MANUAL_SEND_OVERRIDES]
      : LAST_NAME_OVERRIDES,
  };
  // Pro/Enterprise gets the richer template (country + send-date columns); Starter the basic one.
  // The tier split lives in recipientTemplateIsPro so the Step 2 "Send on different dates" download
  // and this importer always agree on which template a plan gets.
  const templateKey = recipientTemplateIsPro(ent)
    ? PUBLIC_ENV.ONESCHEMA_TEMPLATE_PRO
    : PUBLIC_ENV.ONESCHEMA_TEMPLATE_STARTER;

  // The plan's cap, enforced inside the importer rather than after it. Previously a Starter
  // client could upload 2,000 rows and have them silently truncated to 20 on the way out;
  // OneSchema now refuses the surplus where the user can still do something about it. Null is
  // unlimited, so no limit is passed at all.
  const maxRows = ent ? limit(ent, "campaign_send", "max_recipients") : null;
  const rowLimit =
    maxRows === null
      ? {}
      : {
          importMaxRowLimit: maxRows,
          importRowLimitHeader: CONFIGS.recipientCapBlocked.title,
          importRowLimitDescription: CONFIGS.recipientCapBlocked.description,
        };

  useEffect(() => {
    if (isOpen) {
      setVisible(true);
      rafRef.current = requestAnimationFrame(() => setAnimated(true));
    } else {
      setAnimated(false);
      timerRef.current = setTimeout(() => setVisible(false), MODAL_FADE_MS);
    }
    return () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
      if (timerRef.current != null) clearTimeout(timerRef.current);
    };
  }, [isOpen]);

  const handleSuccess = (data: { records: OneSchemaRecord[] }) => {
    const imported: Recipient[] = (data.records ?? []).map((r) => ({
      id: crypto.randomUUID(),
      firstName: r.recipient_first_name ?? "",
      lastName: r.recipient_last_name ?? "",
      email: r.recipient_email ?? "",
      toCompany: r.recipient_company,
      // Homologate a hand-typed CSV value to an ISO code (matches name, alias or code); keep the raw
      // text when nothing matches so the client can see and fix it. Step 4 counts unmapped as US.
      recipientCountry: matchCountryCode(r.recipient_country) ?? r.recipient_country?.trim(),
      senderFirst: r.sender_first_name ?? "",
      senderLast: r.sender_last_name ?? "",
      fromEmail: r.sender_email,
      senderSignature: r.sender_signature,
      senderCompany: r.sender_company ?? "",
      // OneSchema hands dates back as MM/DD/YYYY; the API reads only YYYY-MM-DD / HH:mm and used to
      // save an unreadable value as no date at all (GFG-443). An unreadable value stays raw so the
      // caller can flag it instead of losing it.
      sendDate: isManual
        ? undefined
        : (normalizeSendDate(r.send_on_date) ?? rawOrUndefined(r.send_on_date)),
      sendTime: isManual
        ? undefined
        : (normalizeSendTime(r.send_at_time) ?? rawOrUndefined(r.send_at_time)),
    }));
    onImport(imported);
    onClose();
  };

  if (!visible) return null;

  return (
    <div
      className={`fixed inset-0 z-10000 flex items-center justify-center p-6 bg-black/55 transition-opacity duration-270 ${animated ? "opacity-100" : "opacity-0"}`}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      aria-hidden={!isOpen}
    >
      <div
        className={`relative w-full max-w-275 h-180 max-h-[calc(100vh-48px)] flex flex-col bg-white rounded-xl overflow-hidden shadow-[0_24px_60px_rgba(0,0,0,0.25)] transition-[opacity,transform] duration-270 ${animated ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"}`}
        role="dialog"
        aria-modal="true"
        aria-label="Import Contacts"
      >
        {userJwt && (
          <OneSchemaImporter
            isOpen={isOpen}
            onRequestClose={onClose}
            clientId={PUBLIC_ENV.ONESCHEMA_CLIENT_ID}
            userJwt={userJwt}
            templateKey={templateKey}
            importConfig={{ type: "local" }}
            templateOverrides={templateOverrides}
            // Our OneSchema JWT is identical on every launch, so a saved session resumes forever and
            // keeps the template config it started with, ignoring TEMPLATE_OVERRIDES. Start fresh.
            saveSession={false}
            customizationOverrides={{
              importErrorUX: "blockIfErrors",
              ...IMPORTER_FONT,
              ...rowLimit,
            }}
            devMode={!isProduction()}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              border: "none",
            }}
            onSuccess={handleSuccess}
            onError={(err) => console.error("[OneSchema]", err)}
            onCancel={onClose}
          />
        )}
      </div>
    </div>
  );
}
